import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FaBars, FaUser, FaSignOutAlt, FaChartLine, FaBook, FaUsers, FaMoneyBillWave, FaComments, FaCog, FaShieldAlt, FaHistory, FaClock, FaImage, FaFileAlt } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';

const AdminLayout = ({ children, title = "Admin Dashboard", activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({ fullName: 'Admin User', phone: 'System' });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const loggedInUser = JSON.parse(localStorage.getItem('user'));
        if (loggedInUser && loggedInUser.id) {
          // Admins might also have profile data or we just use what's in localStorage
          setUserData({ fullName: loggedInUser.full_name || 'Admin User', phone: 'System Administrator' });
        }
      } catch (error) {
        console.error("Error fetching admin data:", error);
      }
    };
    fetchUserData();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  const menuItems = [
    { id: 'Overview', label: 'Overview', icon: <FaChartLine /> },
    { id: 'Manage Courses', label: 'Manage Courses', icon: <FaBook /> },
    { id: 'Schedules', label: 'Schedules', icon: <FaClock /> },
    { id: 'Students', label: 'Students', icon: <FaUsers /> },
    { id: 'Payments', label: 'Payments', icon: <FaMoneyBillWave /> },
    { id: 'Gallery', label: 'Manage Gallery', icon: <FaImage /> },
    { id: 'Resources', label: 'Manage Resources', icon: <FaFileAlt /> },
    { id: 'Feedback', label: 'Feedback', icon: <FaComments /> },
    { id: 'Security Settings', label: 'Security Settings', icon: <FaShieldAlt /> },
    { id: 'System History', label: 'System History', icon: <FaHistory /> },
  ];

  return (
    <div className="min-h-screen bg-[#f0f2f5] font-sans text-slate-800">
      
      {/* Top Header */}
      <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white text-xl font-bold border-2 border-white shadow-sm">K</div>
              <div className="flex flex-col">
                <span className="text-orange-500 font-bold leading-none text-lg">KASUN</span>
                <span className="text-orange-500 font-bold leading-none text-lg">WALAKUMBURA</span>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <FaBars className="text-lg cursor-pointer hover:text-blue-600 transition-colors" />
            <span className="font-bold text-sm tracking-tight text-slate-500">{title}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden border border-slate-100">
            <FaUser className="text-slate-400 text-sm" />
          </div>
          <span className="text-xs font-bold text-slate-600 hidden sm:block">{userData.fullName}</span>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-72 p-4 hidden lg:block flex-shrink-0">
          <div className="space-y-4 sticky top-20">
            
            {/* Admin User Card */}
            <div className="bg-white rounded-lg p-6 flex flex-col items-center shadow-sm border border-slate-100">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-4 border-2 border-slate-50 overflow-hidden shadow-inner">
                <FaUser className="text-slate-300 text-3xl" />
              </div>
              <h3 className="font-bold text-sm text-slate-700">{userData.fullName}</h3>
              <p className="text-[10px] text-slate-400 mt-1 font-bold">{userData.phone}</p>
            </div>

            {/* Menu Card */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-100 overflow-hidden">
              <nav className="flex flex-col">
                {menuItems.map((item) => (
                  <button 
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`flex items-center gap-3 w-full px-5 py-4 text-xs font-bold transition-all border-l-4 ${
                      activeTab === item.id 
                      ? 'bg-orange-50 text-orange-600 border-orange-500' 
                      : 'text-slate-400 border-transparent hover:bg-slate-50 hover:text-slate-600'
                    }`}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Logout Card */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-2">
              <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-slate-500 hover:text-red-600 transition-colors">
                <FaSignOutAlt />
                Logout
              </button>
            </div>

            <div className="text-[10px] text-center text-slate-400 font-bold mt-4 uppercase tracking-widest">
              Admin Panel v1.0
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-grow p-4 lg:p-6 min-h-[calc(100vh-64px)]">
          <div className="bg-white rounded-lg shadow-sm border border-slate-100 min-h-full p-6 md:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
